import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/components/providers/auth-provider";
import {
  useAccessLogs,
  useAccessSummary,
  useAccessUsers,
  useSecurityOverview,
  type AccessLogFilters,
} from "@/hooks/use-access";
import { AccessSummaryCards } from "@/components/configuracion/accesos/access-summary-cards";
import { UsersAccessTable } from "@/components/configuracion/accesos/users-access-table";
import { AccessLogTable } from "@/components/configuracion/accesos/access-log-table";
import { SecurityPanel } from "@/components/configuracion/accesos/security-panel";
import { fetchBlobWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

type Tab = "usuarios" | "registro" | "seguridad";

const TABS: { id: Tab; label: string }[] = [
  { id: "usuarios", label: "Usuarios" },
  { id: "registro", label: "Registro de accesos" },
  { id: "seguridad", label: "Seguridad" },
];

/**
 * Panel de accesos — solo `platform_admin` (operador de plataforma).
 *
 * Sirve a dos objetivos con el mismo registro: auditoría ("quién accedió a
 * qué") y adopción ("quién usa la herramienta"). Las pestañas y los filtros
 * viven en la URL para poder compartir un enlace exacto durante un incidente.
 */
export default function AccesosPage() {
  const { isPlatformAdmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = (searchParams.get("tab") as Tab) || "usuarios";

  // Los filtros del registro se derivan de la URL.
  const filters = useMemo<AccessLogFilters>(() => {
    const parsed: AccessLogFilters = {
      page: Number(searchParams.get("page") || 1),
      page_size: 50,
    };
    (["action", "category", "q", "date_from", "user_id", "ip"] as const).forEach((key) => {
      const value = searchParams.get(key);
      if (value) parsed[key] = value;
    });
    return parsed;
  }, [searchParams]);

  const setTab = useCallback(
    (next: Tab) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setFilters = useCallback(
    (next: AccessLogFilters) => {
      const params = new URLSearchParams();
      params.set("tab", "registro");
      Object.entries(next).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "" && key !== "page_size") {
          params.set(key, String(value));
        }
      });
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const summary = useAccessSummary(isPlatformAdmin);
  const users = useAccessUsers(isPlatformAdmin);
  const logs = useAccessLogs(filters, isPlatformAdmin && tab === "registro");
  const security = useSecurityOverview(30, isPlatformAdmin && tab === "seguridad");

  const handleExport = useCallback(async () => {
    try {
      const blob = await fetchBlobWithAuth("/api/v1/admin/access-logs/export.csv?days=30");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `accesos-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      showToast.success("Registro exportado");
    } catch {
      showToast.error("No se pudo exportar el registro");
    }
  }, []);

  // Mismo aviso que el resto de páginas de operador (config LLM, equipos).
  if (!isPlatformAdmin) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <div>
            <h2 className="font-medium text-amber-900">Acceso restringido</h2>
            <p className="mt-1 text-sm text-amber-800">
              El panel de accesos solo está disponible para el operador de la
              plataforma.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900">Accesos y seguridad</h1>
        <p className="mt-1 text-sm text-gray-500">
          Quién entra, qué consulta y qué se lleva de la plataforma.
        </p>
      </header>

      <AccessSummaryCards data={summary.data} isLoading={summary.isLoading} />

      {/* Segmented control propio: no hay tabs de Radix instalados y no merece
          añadir la dependencia para tres botones. */}
      <div
        role="tablist"
        aria-label="Secciones del panel de accesos"
        className="inline-flex rounded-lg border border-gray-200 bg-white p-1"
      >
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === id ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "usuarios" && (
        <UsersAccessTable
          users={users.data}
          isLoading={users.isLoading}
          currentUserId={user?.id}
        />
      )}

      {tab === "registro" && (
        <AccessLogTable
          data={logs.data}
          isLoading={logs.isLoading}
          filters={filters}
          onFiltersChange={setFilters}
          onExport={handleExport}
        />
      )}

      {tab === "seguridad" && (
        <SecurityPanel data={security.data} isLoading={security.isLoading} />
      )}
    </div>
  );
}
